import { FilterAccordions } from "./FilterAccordions";
import { FilterTitles } from "./FilterTitles";
import { FilterRelevancy } from "./FilterRelevancy";
import { Filter } from "./Filter";
import { SortableFilter } from "./SortableFilter";
import { events } from "../events";
import { isActive, isNotActive } from "../toggle/Toggle";

export class FilterSorting {
  private static sortedFilterList: SortableFilter[] = [];

  /*               */

  static register() {
    if (isNotActive("HEUREKA_1988_DEACTIVATE_FRONTEND_FILTERS_SORTING")) {
      events.onFilterSectionLoaded(FilterSorting.init);
    }
  }

  static init() {
    if (
      (!FilterSorting.sortedFilterList || FilterSorting.sortedFilterList.length === 0) &&
      FilterRelevancy.template()?.knownFilterIds.length !== 0 &&
      FilterAccordions.template()?.values.length !== 0
    ) {
      FilterSorting.sortedFilterList = FilterSorting.sortedFilters(FilterAccordions.template()!.values) /*                                                       */
        .filter((f) => !!f) as SortableFilter[];
    }
    FilterAccordions.template()?.sortValues();
    FilterTitles.template()?.sortValues();
  }

  /*                       */

  /*                                                                                                 */
  static denseModeVisibilityComparator = (a: SortableFilter, b: SortableFilter) => {
    const aVisibility = a.isVisibleInDenseMode ? 1 : 0;
    const bVisibility = b.isVisibleInDenseMode ? 1 : 0;
    return bVisibility - aVisibility;
  };

  /*                              */
  static filterRelevancyComparator = (a: SortableFilter, b: SortableFilter) => {
    const aVisibility = a.isRelevant ? 2 : a.isIrrelevant ? 1 : 0;
    const bVisibility = b.isRelevant ? 2 : b.isIrrelevant ? 1 : 0;
    return (bVisibility - aVisibility) / 2; /*                            */
  };

  static alphaNumComparator = (a: SortableFilter, b: SortableFilter) => {
    const minLength = Math.min(a.filterId.length, b.filterId.length);

    for (let i = 0; i < minLength; i++) {
      const diff = a.filterId.charCodeAt(i) - b.filterId.charCodeAt(i);
      if (diff !== 0) return diff;
    }

    /*                                                            */
    return a.filterId.length - b.filterId.length;
  };

  private static sortedFilters(filters: HTMLElement[]): SortableFilter[] {
    let sortedFilters: SortableFilter[] = filters
      .map((f) => {
        const filter = Filter.filterId(f.dataset.filterId!);
        if (filter) {
          return new SortableFilter(
            filter.id,
            filter.isDenseModeFilter,
            filter.isRelevant,
            filter.isIrrelevant,
            filter.forceIndex,
          );
        }
      })
      .filter((f) => !!f) as SortableFilter[];

    const knownFilterIds = FilterRelevancy.template()?.knownFilterIds || [];

    function relevancyScoreComparator(knownFilterIds: string[]): Comparator<SortableFilter> {
      return (a: SortableFilter, b: SortableFilter) => {
        return knownFilterIds.indexOf(a.filterId) - knownFilterIds.indexOf(b.filterId);
      };
    }

    /*           */
    sortedFilters = sortedFilters.sort(
      chainedComparator(
        FilterSorting.denseModeVisibilityComparator,
        FilterSorting.filterRelevancyComparator,
        relevancyScoreComparator(knownFilterIds),
        FilterSorting.alphaNumComparator,
      ),
    );

    sortedFilters = FilterSorting.rearrangeForcedFilters(sortedFilters);

    /*                      */
    const localNav = sortedFilters.find((f) => f.filterId === "localNavigation");
    if (localNav) {
      sortedFilters.unshift(localNav);
    }

    return sortedFilters;
  }

  public static sort<T extends { filterId: string }>(filterTitles: T[]): T[] {
    const sortedFilterIds = isActive("HEUREKA_1991_RENDER_HIDDEN_SELECTED_FILTERS")
      ? FilterSorting.sortedFilterList.map((f) => f.filterId)
      : FilterSorting.sortedFilterList.filter((f) => f.isVisibleInDenseMode || f.isIrrelevant).map((f) => f.filterId);
    return filterTitles
      .filter((f) => !!f.filterId && (sortedFilterIds.indexOf(f.filterId!) >= 0 || f.filterId === "localNavigation"))
      .sort((a, b) => {
        return sortedFilterIds.indexOf(a.filterId!) - sortedFilterIds.indexOf(b.filterId!);
      });
  }

  private static rearrangeForcedFilters(filters: SortableFilter[]): SortableFilter[] {
    const filtersWithForcedIndex = filters.filter((f) => f.forceIndex != undefined && f.forceIndex > -1);

    filtersWithForcedIndex.forEach((f) => FilterSorting.move(f, filters, f.forceIndex!));
    return filters;
  }

  private static move(sortableFilter: SortableFilter, filters: SortableFilter[], toIndex: number) {
    filters.splice(filters.indexOf(sortableFilter), 1);
    filters.splice(toIndex, 0, sortableFilter);
  }
}

type Comparator<T> = (a: T, b: T) => number; /*         */

/**
 *
 */
function chainedComparator<T>(...comparators: Comparator<T>[]): Comparator<T> {
  return (a: T, b: T): number => {
    for (const comparator of comparators) {
      const result = comparator(a, b);
      if (result !== 0) return result;
    }
    return 0; /*                                      */
  };
}
